import React, { useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Carousel } from "../Carousel/Carousel";
import { GameCard } from "../GameCard/GameCard";
import CarmenImage from "../../assets/images/carmen.png";
import { useAlertDialog } from "../ConfirmDialog/ConfirmDialogProvider";
import resolveMediaUrl from "../../utils/resolveMediaUrl";
import { buildGamePath, buildProfilePath } from "../../utils/appRoutes";
import "./Frame.css";

export const Frame = ({ games = [], loading = false }) => {
  const carouselRef = useRef(null);
  const scrollIntervalRef = useRef();
  const showAlert = useAlertDialog();
  const navigate = useNavigate();

  /* ---------------- auto‑scroll helpers ---------------- */
  const stopAutoScroll = () => clearInterval(scrollIntervalRef.current);

  const scrollRight = () => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const cardWidth = carousel.offsetWidth / 6;
    const maxScrollLeft = carousel.scrollWidth - carousel.offsetWidth;

    if (carousel.scrollLeft + carousel.offsetWidth < carousel.scrollWidth - cardWidth) {
      carousel.scrollBy({ left: cardWidth, behavior: "smooth" });
    } else {
      carousel.scrollTo({ left: maxScrollLeft, behavior: "smooth" });
      setTimeout(() => carousel.scrollTo({ left: 0, behavior: "smooth" }), 3000);
    }
  };

  const startAutoScroll = useCallback(() => {
    stopAutoScroll();
    scrollIntervalRef.current = setInterval(scrollRight, 3000);
  }, []);

  /* restart scrolling whenever games change */
  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.scrollLeft = 0;
      startAutoScroll();
    }
    return stopAutoScroll;
  }, [games, startAutoScroll]);

  /* -------- signup handler (unchanged) -------- */
  const handleSignup = async (gameId) => {
    const idToken = localStorage.getItem("idToken");
    if (!idToken) {
      await showAlert({
        title: "SIGN UP",
        message: "Please log in to sign up for this game.",
      });
      return;
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/games/signup/${gameId}`,
        {},
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      await showAlert({
        title: "SIGN UP",
        message: response?.data?.message || "Signed up successfully!",
      });
      navigate("/");
    } catch (err) {
      console.error("Signup error:", err);
      const msg = err.response?.data?.error || "Signup failed.";
      await showAlert({
        title: "SIGN UP",
        message: msg,
      });
      if (msg.toLowerCase().includes("not enough credits")) {
        navigate(
          buildProfilePath("subscription", { next: buildGamePath(gameId) })
        );
      }
    }
  };

  return (
    <div
      className="frame"
      onMouseEnter={stopAutoScroll}
      onMouseLeave={startAutoScroll}
    >
      <div className="carousel-header">
        <button className="carousel-nav left" onClick={() => carouselRef.current?.scrollBy({ left: -200, behavior: "smooth" })}>
          &lt;
        </button>
        <h2 className="carousel-title">UPCOMING GAMES</h2>
        <button className="carousel-nav right" onClick={() => carouselRef.current?.scrollBy({ left: 200, behavior: "smooth" })}>
          &gt;
        </button>
      </div>

      {loading ? (
        <p className="no-games-message">Loading…</p>
      ) : games.length ? (
        <Carousel ref={carouselRef} className={games.length <= 5 ? "centered" : ""}>
          {games.map((g) => {
            const img = resolveMediaUrl(g.stadium?.stadiumPicturePath, CarmenImage);
            return (
              <GameCard
                key={g._id}
                style={{ width: "140px", height: "174px" }}
                imageSrc={img}
                gameName={g.stadium?.name || "Unknown Stadium"}
                gameSubtitle={g.type}
                gameDay={g.date}
                gameTime={g.time}
                onSignup={() => handleSignup(g._id)}
                gameId={g._id}
              />
            );
          })}
        </Carousel>
      ) : (
        <p className="no-games-message">No upcoming games available.</p>
      )}
    </div>
  );
};

export default Frame;
